// Browser Notification Service for GNONS CRM
// Fires a browser notification when a follow-up reminder becomes due.
// Requirements:
// - User pref (notifications_enabled=true) AND global setting (enabled=true) must both be true
// - Dedupe with sessionStorage so the same follow-up doesn't re-notify on every poll
// - Clicking the notification navigates to the related lead

const FIRED_KEY = 'gnons_fired_notifications';

const isBrowserSupported = () => typeof window !== 'undefined' && 'Notification' in window;

export const getPermission = () => (isBrowserSupported() ? Notification.permission : 'denied');

export const requestPermission = async () => {
  if (!isBrowserSupported()) return 'denied';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  const result = await Notification.requestPermission();
  return result;
};

const getFiredSet = () => {
  try {
    const raw = sessionStorage.getItem(FIRED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const saveFiredSet = (set) => {
  try {
    sessionStorage.setItem(FIRED_KEY, JSON.stringify([...set]));
  } catch {
    // ignore quota errors
  }
};

/**
 * Given a list of follow-ups, fire browser notifications for any that are due now.
 * "Due" means: not completed, and (scheduled_date - reminder_before_minutes) <= now < (scheduled_date + 5 minutes).
 * Deduped via sessionStorage.
 *
 * @param {Array} followUps - follow-up objects from /api/follow-ups
 * @param {Object} opts
 * @param {boolean} opts.globalEnabled - global admin toggle
 * @param {boolean} opts.userEnabled - per-user toggle
 * @param {Function} opts.onClick - function(leadId) called when notification clicked
 */
export const checkAndFireNotifications = (followUps, { globalEnabled, userEnabled, onClick } = {}) => {
  if (!isBrowserSupported()) return;
  if (Notification.permission !== 'granted') return;
  if (!globalEnabled || !userEnabled) return;
  if (!Array.isArray(followUps)) return;

  const now = Date.now();
  const fired = getFiredSet();
  let changed = false;

  followUps.forEach((fu) => {
    if (fu.completed) return;
    if (fired.has(fu.id)) return;

    const scheduled = new Date(fu.scheduled_date).getTime();
    const reminderBefore = (fu.reminder_before || 0) * 60 * 1000;
    const dueFrom = scheduled - reminderBefore;
    const dueUntil = scheduled + 5 * 60 * 1000; // 5-minute grace window

    if (now >= dueFrom && now <= dueUntil) {
      try {
        const title = `Follow-up: ${fu.lead_name || 'Lead'}`;
        const body = `${(fu.follow_up_type || 'reminder').replace(/_/g, ' ')} at ${new Date(fu.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}${fu.notes ? ` — ${fu.notes}` : ''}`;
        const n = new Notification(title, {
          body,
          tag: `gnons-fu-${fu.id}`,
          icon: '/favicon.ico',
          requireInteraction: true
        });
        n.onclick = (e) => {
          e.preventDefault();
          window.focus();
          if (typeof onClick === 'function') onClick(fu.lead_id);
          n.close();
        };
        fired.add(fu.id);
        changed = true;
      } catch {
        // Silent — some browsers block Notification constructor without user gesture
      }
    }
  });

  if (changed) saveFiredSet(fired);
};

export const clearFiredHistory = () => {
  try {
    sessionStorage.removeItem(FIRED_KEY);
  } catch {
    // ignore
  }
};

const DELAYED_FIRED_KEY = 'gnons_fired_delayed_nudges';

const getDelayedFiredSet = () => {
  try {
    const raw = sessionStorage.getItem(DELAYED_FIRED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
};

const saveDelayedFiredSet = (set) => {
  try { sessionStorage.setItem(DELAYED_FIRED_KEY, JSON.stringify([...set])); } catch {}
};

/**
 * Fire a browser notification once per session for each newly-delayed project.
 * Reuses the same browser Notification permission + user/global toggles.
 * @param {Object} delayedResponse - response from /api/dashboard/delayed-projects
 * @param {Object} opts
 * @param {boolean} opts.globalEnabled
 * @param {boolean} opts.userEnabled
 * @param {Function} opts.onClick - function(leadId)
 */
export const checkAndFireDelayedNudges = (delayedResponse, { globalEnabled, userEnabled, onClick } = {}) => {
  if (!isBrowserSupported()) return;
  if (Notification.permission !== 'granted') return;
  if (!globalEnabled || !userEnabled) return;
  if (!delayedResponse || !Array.isArray(delayedResponse.services)) return;

  const fired = getDelayedFiredSet();
  let changed = false;

  delayedResponse.services.forEach((svc) => {
    (svc.projects || []).forEach((p) => {
      if (fired.has(p.id)) return;
      try {
        const title = `Delayed project: ${p.customer_name}`;
        const body = `${svc.service_name} · ${p.days_delayed}d delayed${p.delay_reasons?.length ? ` — ${p.delay_reasons[0]}` : ''}`;
        const n = new Notification(title, {
          body,
          tag: `gnons-delayed-${p.id}`,
          icon: '/favicon.ico',
          requireInteraction: true
        });
        n.onclick = (e) => {
          e.preventDefault();
          window.focus();
          if (typeof onClick === 'function') onClick(p.id);
          n.close();
        };
        fired.add(p.id);
        changed = true;
      } catch { /* silent */ }
    });
  });

  if (changed) saveDelayedFiredSet(fired);
};
